import { RehireRecommendation } from '@/common/graphql';

import type { ComboboxItemWithFormatted } from '../props';
import { SelectMultiple, type SelectMultipleProps } from '../SelectMultiple';

interface RehireRecommendationSelectMultipleProps
  extends Omit<SelectMultipleProps<ComboboxItemWithFormatted<RehireRecommendation>>, 'searchable' | 'value'> {
  value: Array<RehireRecommendation> | null | undefined;
}

// FR-001's exact selectable-value wording (Strongly Recommended / Conditional / Not Recommended) -
// intentionally not the longer descriptive `RehireRecommendationLabels` used on the Off-boarding
// edit form's single-select (`RehireRecommendationSelectChip`), which serves a different purpose.
const labels: Record<RehireRecommendation, string> = {
  [RehireRecommendation.StronglyRecommended]: 'Strongly Recommended',
  [RehireRecommendation.Conditional]: 'Conditional',
  [RehireRecommendation.NotRecommended]: 'Not Recommended',
};

const sortOrder = [
  RehireRecommendation.StronglyRecommended,
  RehireRecommendation.Conditional,
  RehireRecommendation.NotRecommended,
];

// Deliberately not `ObjectUtils.arrayFromEnumValues(RehireRecommendation)` (the sibling
// EmployeeStatusSelect.tsx convention): importing `@/common/utils` from this file drags a
// pre-existing circular load into `@/common/types/filters/demographic-report-filter.ts` (which
// needs `DateUtils` back from `@/common/utils`) that only surfaces when a test importing this
// component is the module graph's entry point, requiring an increasingly fragile multi-member
// `vi.mock('@/common/utils', ...)` to work around. `sortOrder` is this enum's complete,
// intentionally-ordered value set (verified against `RehireRecommendation`'s 3 members).
const data = sortOrder.map(value => ({ label: labels[value], value }));

export const RehireRecommendationSelectMultiple = ({
  placeholder = 'Select recommendation',
  ...rest
}: RehireRecommendationSelectMultipleProps) => (
  <SelectMultiple
    {...rest}
    data={data}
    loading={false}
    multipleSelectedLabel={length => `${length} recommendations`}
    placeholder={placeholder}
    searchable={false}
  />
);
